import { UserService } from "../../services/user.service.js";

export const UserListModal = {
    show: async (initialTab = 'followers', targetUserId, onClose) => {
        // Create Modal Overlay
        const overlay = document.createElement('div');
        overlay.className = "fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in";
        overlay.onclick = (e) => {
            if (e.target === overlay) {
                closeModal();
            }
        };

        const modal = document.createElement('div');
        modal.className = "bg-white w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl flex flex-col max-h-[80vh] animate-slide-up";
        overlay.appendChild(modal);

        // State
        let currentTab = initialTab;
        let userList = [];
        let isLoading = true;

        const closeModal = () => {
            // Animate out
            modal.classList.remove('animate-slide-up');
            modal.classList.add('animate-slide-down');
            overlay.classList.remove('animate-fade-in');
            overlay.classList.add('animate-fade-out');
            setTimeout(() => {
                overlay.remove();
                if (onClose) onClose();
            }, 200);
        };

        // Render Tabs
        const renderHeader = () => {
            return `
                <div class="flex border-b border-gray-100">
                    <button class="flex-1 py-4 text-sm font-bold transition-colors ${currentTab === 'followers' ? 'text-black border-b-2 border-black' : 'text-gray-400 hover:text-gray-600'}" data-tab="followers">
                        팔로워
                    </button>
                    <button class="flex-1 py-4 text-sm font-bold transition-colors ${currentTab === 'following' ? 'text-black border-b-2 border-black' : 'text-gray-400 hover:text-gray-600'}" data-tab="following">
                        팔로잉
                    </button>
                </div>
            `;
        };

        // Render List
        const renderList = async () => {
            const listContainer = document.createElement('div');
            listContainer.className = "flex-1 overflow-y-auto p-0 min-h-[300px] bg-white";

            if (isLoading) {
                listContainer.innerHTML = `<div class="flex justify-center py-10"><span class="material-symbols-outlined animate-spin text-gray-300">progress_activity</span></div>`;
                return listContainer;
            }

            if (userList.length === 0) {
                listContainer.innerHTML = `<div class="flex flex-col items-center justify-center h-full text-gray-400 text-sm">
                    <span class="material-symbols-outlined mb-2 text-3xl opacity-50">group_off</span>
                    ${currentTab === 'followers' ? '아직 팔로워가 없습니다.' : '팔로우하는 유저가 없습니다.'}
                </div>`;
                return listContainer;
            }

            // Fetch my following list to check status for buttons (if I am logged in)
            const myUid = window.user?.uid;
            let myFollowingIds = new Set();
            if (myUid) {
                const myFollowing = await UserService.getFollowing(myUid);
                myFollowing.forEach(u => myFollowingIds.add(u.uid));
            }

            userList.forEach(user => {
                const item = document.createElement('div');
                item.className = "flex items-center justify-between p-4 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-50 last:border-0";

                // Logic: Navigation
                item.onclick = (e) => {
                    // Prevent navigation if clicking the Follow button
                    if (e.target.closest('button')) return;

                    closeModal(); // Close first
                    // Trigger Navigation
                    // If router logic handles same-route params change correctly, this works.
                    // If not, we might need to force reload or handle router update.
                    window.router.navigate(`/user/${user.uid}`);
                };

                const isMe = myUid === user.uid;
                const isFollowing = myFollowingIds.has(user.uid);

                // Button HTML
                let btnHtml = '';
                if (!isMe && myUid) {
                    btnHtml = isFollowing
                        ? `<button class="text-xs bg-gray-100 text-gray-600 px-3 py-1.5 rounded-full font-bold hover:bg-gray-200 transition-colors btn-follow" data-uid="${user.uid}" data-action="unfollow">팔로잉</button>`
                        : `<button class="text-xs bg-indigo-600 text-white px-3 py-1.5 rounded-full font-bold hover:bg-indigo-700 transition-colors btn-follow" data-uid="${user.uid}" data-action="follow">팔로우</button>`;
                }

                const photo = user.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.uid}`;

                item.innerHTML = `
                    <div class="flex items-center gap-3">
                        <div class="w-10 h-10 rounded-full bg-gray-200 overflow-hidden border border-gray-100">
                            <img src="${photo}" class="w-full h-full object-cover">
                        </div>
                        <div class="flex flex-col">
                            <span class="text-sm font-bold text-gray-900">${user.displayName || '알 수 없음'}</span>
                        </div>
                    </div>
                    <div>${btnHtml}</div>
                `;

                // Bind Follow Button Logic (Optimistic UI)
                const btn = item.querySelector('.btn-follow');
                if (btn) {
                    btn.onclick = async () => {
                        const action = btn.dataset.action;
                        const targetUid = btn.dataset.uid;

                        // Optimistic Update
                        if (action === 'follow') {
                            btn.className = "text-xs bg-gray-100 text-gray-600 px-3 py-1.5 rounded-full font-bold hover:bg-gray-200 transition-colors btn-follow";
                            btn.textContent = "팔로잉";
                            btn.dataset.action = "unfollow";
                            try {
                                await UserService.followUser(window.user, { uid: targetUid, displayName: user.displayName, photoURL: user.photoURL });
                            } catch (e) {
                                console.error(e);
                                btn.className = "text-xs bg-indigo-600 text-white px-3 py-1.5 rounded-full font-bold hover:bg-indigo-700 transition-colors btn-follow";
                                btn.textContent = "팔로우";
                                btn.dataset.action = "follow";
                                alert("팔로우 실패");
                            }
                        } else {
                            btn.className = "text-xs bg-indigo-600 text-white px-3 py-1.5 rounded-full font-bold hover:bg-indigo-700 transition-colors btn-follow";
                            btn.textContent = "팔로우";
                            btn.dataset.action = "follow";
                            try {
                                await UserService.unfollowUser(window.user.uid, targetUid);
                            } catch (e) {
                                console.error(e);
                                btn.className = "text-xs bg-gray-100 text-gray-600 px-3 py-1.5 rounded-full font-bold hover:bg-gray-200 transition-colors btn-follow";
                                btn.textContent = "팔로잉";
                                btn.dataset.action = "unfollow";
                                alert("언팔로우 실패");
                            }
                        }
                    };
                }

                listContainer.appendChild(item);
            });

            return listContainer;
        };

        const updateContent = async () => {
            const container = modal.querySelector('#user-list-content');
            container.innerHTML = '';

            // Header
            modal.querySelector('#modal-header').innerHTML = renderHeader();

            // Bind Tab Clicks
            modal.querySelectorAll('button[data-tab]').forEach(btn => {
                btn.onclick = () => {
                    currentTab = btn.dataset.tab;
                    isLoading = true;
                    updateContent();
                };
            });

            // Fetch Data
            try {
                if (currentTab === 'followers') {
                    userList = await UserService.getFollowers(targetUserId);
                } else {
                    userList = await UserService.getFollowing(targetUserId);
                }
                isLoading = false;
            } catch (e) {
                console.error(e);
                isLoading = false;
                userList = [];
            }

            container.appendChild(await renderList());
        };

        // Initial Layout
        modal.innerHTML = `
            <div id="modal-header"></div>
            <div id="user-list-content" class="flex-1 overflow-hidden flex flex-col min-h-[400px]"></div>
            <div class="p-4 border-t border-gray-100 flex justify-center">
                <button id="close-modal-btn" class="text-gray-500 hover:text-gray-900 text-sm">닫기</button>
            </div>
        `;

        modal.querySelector('#close-modal-btn').onclick = closeModal;

        document.body.appendChild(overlay);

        // Start
        updateContent();
    }
};
